import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Star, MapPin, Clock, Store, Sparkle, X, Gem, CalendarClock, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const CATS = [["", "Tutte"], ["spa", "Spa & Benessere"], ["fiori", "Fioristi"], ["evento", "Eventi"], ["professionista", "Professionisti"], ["turismo", "Turismo"], ["sport", "Sport"], ["servizio", "Servizi"]];
const MODES = { at_provider: "In sede", at_home: "A domicilio", online: "Online", external: "Luogo esterno" };

function priceLabel(s) {
  if (s.pricing_mode === "quote") return "Preventivo";
  if (s.pricing_mode === "from") return `da €${Number(s.price || 0).toFixed(0)}`;
  return `€${Number(s.price || 0).toFixed(2)}`;
}

export default function ServicesShowcase() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({ category: "", zone: "", price_min: "", price_max: "", date: "", mode: "", min_rating: "" });
  const [sort, setSort] = useState("relevance");
  const [season, setSeason] = useState(null);
  const [near, setNear] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const all = { ...f, sort };
    if (near) { all.near_lat = near.lat; all.near_lng = near.lng; }
    const params = Object.entries(all).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    try { const { data } = await api.get(`/experiences/services${params ? "?" + params : ""}`); setItems(data.items || []); setSeason(data.season || null); }
    catch { setItems([]); } finally { setLoading(false); }
  }, [f, sort, near]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => { setF({ category: "", zone: "", price_min: "", price_max: "", date: "", mode: "", min_rating: "" }); setNear(null); setSort("relevance"); };

  const SORTS = [["relevance", "Consigliati"], ["most_requested", "Più richiesti"], ["best_rated", "Migliori recensioni"], ["trending", "Tendenza"], ["new", "Novità"], ["distance", "Vicino a me"]];
  const pickSort = (k) => {
    if (k === "distance" && !near) {
      if (navigator.geolocation) navigator.geolocation.getCurrentPosition(
        (pos) => { setNear({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setSort("distance"); },
        () => toast.error("Attiva la geolocalizzazione per ordinare per distanza"));
      else toast.error("Geolocalizzazione non disponibile");
    } else setSort(k);
  };

  return (
    <div data-testid="services-showcase">
      {season && (
        <div className="mb-3 rounded-xl bg-gradient-to-r from-[#7DF9FF]/10 to-[#00E676]/10 border border-[#7DF9FF]/20 px-4 py-2.5 flex items-center gap-2" data-testid="vetrina-season">
          <Sparkle className="h-4 w-4 text-[#7DF9FF] shrink-0" />
          <span className="text-sm text-zinc-200"><b className="text-[#7DF9FF]">In evidenza ora:</b> {season.label}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        {CATS.map(([k, l]) => (
          <button key={k || "all"} data-testid={`vetrina-cat-${k || "all"}`} onClick={() => set("category", k)}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${f.category === k ? "bg-[#00E676] text-black border-[#00E676]" : "bg-white/5 text-zinc-300 border-white/15"}`}>{l}</button>
        ))}
        <button data-testid="vetrina-filters-toggle" onClick={() => setShowFilters((s) => !s)} className="ml-auto text-xs flex items-center gap-1 rounded-full px-3 py-1.5 border border-white/15 bg-white/5"><SlidersHorizontal className="h-3.5 w-3.5" /> Filtri</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="vetrina-sorts">
        {SORTS.map(([k, l]) => (
          <button key={k} data-testid={`vetrina-sort-${k}`} onClick={() => pickSort(k)}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${sort === k ? "bg-[#7DF9FF] text-black border-[#7DF9FF]" : "bg-white/5 text-zinc-400 border-white/15"}`}>{l}</button>
        ))}
      </div>

      {showFilters && (
        <div className="bg-[#141417] border border-white/10 rounded-xl p-4 mb-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="vetrina-filters">
          <div><label className="text-xs text-zinc-400">Comune</label><Input data-testid="filter-zone" value={f.zone} onChange={(e) => set("zone", e.target.value)} placeholder="Es. Ciriè" className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
          <div><label className="text-xs text-zinc-400">Prezzo min €</label><Input data-testid="filter-pmin" type="number" value={f.price_min} onChange={(e) => set("price_min", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
          <div><label className="text-xs text-zinc-400">Prezzo max €</label><Input data-testid="filter-pmax" type="number" value={f.price_max} onChange={(e) => set("price_max", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
          <div><label className="text-xs text-zinc-400">Data</label><Input data-testid="filter-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1 h-9" /></div>
          <div><label className="text-xs text-zinc-400">Modalità</label>
            <select data-testid="filter-mode" value={f.mode} onChange={(e) => set("mode", e.target.value)} className="w-full bg-[#0A0A0C] border border-white/10 rounded-md px-2 mt-1 h-9 text-sm">
              <option value="">Tutte</option>{Object.entries(MODES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          <div><label className="text-xs text-zinc-400">Valutazione min</label>
            <select data-testid="filter-rating" value={f.min_rating} onChange={(e) => set("min_rating", e.target.value)} className="w-full bg-[#0A0A0C] border border-white/10 rounded-md px-2 mt-1 h-9 text-sm">
              <option value="">Qualsiasi</option><option value="3">3★+</option><option value="4">4★+</option><option value="4.5">4.5★+</option>
            </select></div>
          <button onClick={reset} data-testid="filter-reset" className="text-xs text-zinc-400 hover:text-white underline col-span-full text-left">Azzera filtri</button>
        </div>
      )}

      {loading ? <div className="text-zinc-500 py-14 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /> Caricamento…</div> : (
        items.length === 0 ? <div className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl" data-testid="vetrina-empty">Nessun servizio disponibile con questi filtri.</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((s) => (
              <div key={s.id} data-testid={`vetrina-card-${s.id}`} className="bg-[#141417] border border-white/10 rounded-2xl overflow-hidden flex flex-col hover:border-[#7DF9FF]/40 transition-colors">
                <div className="h-32 bg-cover bg-center bg-[#1C1C1F] relative" style={s.images?.[0] ? { backgroundImage: `url(${s.images[0]})` } : {}}>
                  {!s.images?.[0] && <div className="absolute inset-0 flex items-center justify-center text-[#7DF9FF]/40"><Sparkle className="h-10 w-10" /></div>}
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-black/60 text-[#7DF9FF] border border-[#7DF9FF]/30">{s.category}</span>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="font-semibold">{s.title}</div>
                  <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5"><Store className="h-3 w-3" /> {s.provider_name}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-zinc-400">
                    {s.zone && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.zone}</span>}
                    {typeof s.distance_km === "number" && <span className="flex items-center gap-1 text-[#7DF9FF]"><MapPin className="h-3 w-3" /> {s.distance_km} km</span>}
                    {s.duration_min ? <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {s.duration_min}min</span> : null}
                    <span>{MODES[s.location_mode] || s.location_mode}</span>
                    {s.reviews_count > 0 && <span className="flex items-center gap-1 text-[#FFD700]"><Star className="h-3 w-3 fill-[#FFD700]" /> {s.rating} ({s.reviews_count})</span>}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                    <span className="text-[#00E676] font-bold">{priceLabel(s)}</span>
                    <Button size="sm" data-testid={`vetrina-view-${s.id}`} onClick={() => setDetail(s.id)} className="bg-[#00E676] text-black rounded-full h-8">Vedi / Prenota</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {detail && <ServiceDetailDialog oid={detail} onClose={() => setDetail(null)} user={user} navigate={navigate} />}
    </div>
  );
}

function ServiceDetailDialog({ oid, onClose, user, navigate }) {
  const [d, setD] = useState(null);
  const [slots, setSlots] = useState({ date: "", list: [], time: "", loading: false });
  const [useCredits, setUseCredits] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/experiences/services/${oid}/detail`).then(({ data }) => setD(data)).catch(() => onClose()); }, [oid, onClose]);

  const loadSlots = async (date) => {
    setSlots((p) => ({ ...p, date, loading: true, time: "" }));
    try { const { data } = await api.get(`/experiences/services/${oid}/slots?date=${date}`); setSlots((p) => ({ ...p, list: data.slots || [], loading: false })); }
    catch { setSlots((p) => ({ ...p, list: [], loading: false })); }
  };

  const book = async () => {
    if (!user) { navigate("/login", { state: { from: { pathname: "/esperienze" } } }); return; }
    if (d.flow_mode === "slot_booking" && (!slots.date || !slots.time)) { toast.error("Scegli data e ora"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/experiences/services/${oid}/book`, {
        slot: d.flow_mode === "slot_booking" ? { date: slots.date, time: slots.time } : {},
        credits_used: useCredits ? (user?.credits || 0) : 0, address: user?.address || "", origin_url: window.location.origin,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Prenotazione confermata! 🎉"); navigate(`/app/esperienze/${data.experience_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Prenotazione non riuscita"); setBusy(false); }
  };

  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141417] border border-white/10 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vetrina-detail">
        {!d ? <div className="p-8 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
          <>
            <div className="h-40 bg-cover bg-center bg-[#1C1C1F] relative" style={d.images?.[0] ? { backgroundImage: `url(${d.images[0]})` } : {}}>
              {!d.images?.[0] && <div className="absolute inset-0 flex items-center justify-center text-[#7DF9FF]/40"><Sparkle className="h-12 w-12" /></div>}
              <button onClick={onClose} className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/60 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <div className="font-display font-bold text-lg">{d.title}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-1"><Store className="h-3 w-3" /> {d.provider_name} · {d.category}</div>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400">
                {d.zone && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {d.zone}</span>}
                {d.duration_min ? <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {d.duration_min} min</span> : null}
                <span>{MODES[d.location_mode] || d.location_mode}</span>
                {d.reviews_count > 0 && <span className="flex items-center gap-1 text-[#FFD700]"><Star className="h-3 w-3 fill-[#FFD700]" /> {d.rating} ({d.reviews_count})</span>}
              </div>
              {d.description && <p className="text-sm text-zinc-300">{d.description}</p>}
              {d.inclusions && <div className="text-xs text-zinc-400"><b className="text-zinc-300">Include:</b> {d.inclusions}</div>}
              {d.cancellation_policy && <div className="text-xs text-zinc-400"><b className="text-zinc-300">Cancellazione:</b> {d.cancellation_policy}</div>}

              {d.flow_mode === "slot_booking" && (
                <div className="rounded-xl bg-[#0A0A0C] border border-white/10 p-3">
                  <div className="text-sm font-semibold flex items-center gap-1.5 mb-2"><CalendarClock className="h-4 w-4 text-[#7DF9FF]" /> Scegli data e ora</div>
                  <Input type="date" min={today} value={slots.date} onChange={(e) => loadSlots(e.target.value)} className="bg-[#141417] border-white/10 h-9" data-testid="vetrina-slot-date" />
                  {slots.loading && <div className="text-xs text-zinc-500 mt-2"><Loader2 className="h-3 w-3 animate-spin inline" /> Carico gli orari…</div>}
                  {slots.date && !slots.loading && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {slots.list.filter((x) => x.available).length === 0 && <span className="text-xs text-zinc-500">Nessuno slot libero.</span>}
                      {slots.list.filter((x) => x.available).map((x) => (
                        <button key={x.time} data-testid={`vetrina-slot-${x.time}`} onClick={() => setSlots((p) => ({ ...p, time: x.time }))}
                          className={`text-xs rounded-md px-2.5 py-1 border ${slots.time === x.time ? "bg-[#00E676] text-black border-[#00E676]" : "bg-white/5 text-zinc-300 border-white/15"}`}>{x.time}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {d.flow_mode !== "slot_booking" && (
                <div className="text-xs text-zinc-400 rounded-lg bg-[#7DF9FF]/5 border border-[#7DF9FF]/20 px-3 py-2">
                  {d.flow_mode === "contact_request" ? "Invii una richiesta: il fornitore ti contatterà per confermare." : "Conferma immediata alla prenotazione."}
                </div>
              )}

              {(user?.credits || 0) > 0 && d.pricing_mode !== "quote" && (
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="vetrina-use-credits">
                  <input type="checkbox" checked={useCredits} onChange={(e) => setUseCredits(e.target.checked)} className="h-4 w-4 accent-[#00E676]" />
                  <Gem className="h-4 w-4 text-[#C4B5FD]" /> Usa i miei {user.credits} crediti
                </label>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-[#00E676] font-bold text-lg">{priceLabel(d)}</span>
                <Button data-testid="vetrina-book" onClick={book} disabled={busy} className="bg-[#00E676] text-black rounded-full font-semibold">
                  {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Attendi…</> : (d.pricing_mode === "quote" ? "Richiedi preventivo" : "Prenota")}
                </Button>
              </div>

              {(d.reviews || []).length > 0 && (
                <div className="pt-3 border-t border-white/10">
                  <div className="text-sm font-semibold mb-2">Recensioni</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {d.reviews.map((r) => (
                      <div key={r.id} className="text-xs bg-[#0A0A0C] border border-white/10 rounded-lg p-2.5">
                        <div className="flex items-center gap-1 text-[#FFD700]">{"★".repeat(r.rating)}<span className="text-zinc-400 ml-1">{r.customer_name}</span></div>
                        {r.comment && <p className="text-zinc-300 mt-1">{r.comment}</p>}
                        {r.provider_reply && <p className="text-zinc-400 mt-1 pl-2 border-l-2 border-[#7DF9FF]/40"><b>Risposta:</b> {r.provider_reply}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
